import { useEffect, useState } from 'react';
import {
  AlertTriangle,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  ImageOff,
  MessageSquarePlus,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { useSignedUrl } from '@/features/workMode/hooks';
import type { WorkInstructionStep, WorkInstructionWithSteps } from '@/features/workMode/types';
import { formatDuration } from '@/features/workMode/api';

interface WorkInstructionsPanelProps {
  instruction: WorkInstructionWithSteps | null | undefined;
  loading?: boolean;
  onSuggestChange?: (step: WorkInstructionStep | null) => void;
  className?: string;
}

/**
 * Step-by-step work instructions: one big photo per step, swipe / tap through,
 * with a "fix the instructions" button on every step. Nothing is checked off —
 * only the finished unit gets logged. The bench places this in its own panel
 * beside the timer on a wide screen and under it on a phone.
 */
export function WorkInstructionsPanel({
  instruction,
  loading,
  onSuggestChange,
  className,
}: WorkInstructionsPanelProps) {
  const steps = instruction?.steps ?? [];
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    setIdx(0);
  }, [instruction?.id]);

  // Swipe between steps on touch.
  const [touchX, setTouchX] = useState<number | null>(null);

  if (loading) {
    return (
      <div
        role="region"
        aria-label="Work instructions"
        className={cn(
          'flex h-full min-h-0 flex-col gap-3 overflow-y-auto rounded-2xl border border-border bg-card p-4',
          className,
        )}
      >
        <Skeleton className="h-5 w-40" />
        <Skeleton className="flex-1 rounded-xl" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (!instruction || steps.length === 0) {
    return (
      <div
        role="region"
        aria-label="Work instructions"
        className={cn(
          'flex h-full min-h-0 flex-col items-center justify-center gap-3 overflow-y-auto rounded-2xl border border-dashed border-border bg-card/60 p-6 text-center',
          className,
        )}
      >
        <BookOpen className="h-8 w-8 text-muted-foreground" />
        <div>
          <p className="font-medium text-foreground">No work instructions yet</p>
          {instruction && (
            <p className="text-sm text-muted-foreground">This set has no steps.</p>
          )}
        </div>
        {onSuggestChange && (
          <Button variant="outline" onClick={() => onSuggestChange(null)}>
            <MessageSquarePlus className="h-4 w-4" />
            Ask for instructions
          </Button>
        )}
      </div>
    );
  }

  const step = steps[Math.min(idx, steps.length - 1)];
  const go = (d: number) => setIdx((i) => Math.max(0, Math.min(steps.length - 1, i + d)));

  return (
    <div
      role="region"
      aria-label="Work instructions"
      className={cn(
        'flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-border bg-card',
        className,
      )}
      onTouchStart={(e) => setTouchX(e.touches[0]?.clientX ?? null)}
      onTouchEnd={(e) => {
        if (touchX === null) return;
        const dx = (e.changedTouches[0]?.clientX ?? touchX) - touchX;
        if (Math.abs(dx) > 48) go(dx < 0 ? 1 : -1);
        setTouchX(null);
      }}
    >
      <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-4 py-2.5">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-foreground">{instruction.title}</p>
          <p className="text-xs text-muted-foreground">
            Step {idx + 1} of {steps.length}
            {step.est_seconds ? ` · about ${formatDuration(step.est_seconds)}` : ''}
          </p>
        </div>
        {onSuggestChange && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onSuggestChange(step)}
            className="shrink-0"
          >
            <MessageSquarePlus className="h-4 w-4" />
            Suggest a change
          </Button>
        )}
      </div>

      <div className="relative min-h-[4.5rem] flex-1 basis-0 bg-secondary/50">
        <StepImage path={step.image_path} alt={step.title} />
        <button
          type="button"
          onClick={() => go(-1)}
          disabled={idx === 0}
          aria-label="Previous step"
          className="absolute left-2 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-card/90 text-foreground shadow disabled:opacity-30"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
        <button
          type="button"
          onClick={() => go(1)}
          disabled={idx === steps.length - 1}
          aria-label="Next step"
          className="absolute right-2 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-card/90 text-foreground shadow disabled:opacity-30"
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      </div>

      <div className="max-h-[46%] shrink-0 space-y-2 overflow-y-auto px-4 pb-3 pt-3">
        <div className="flex items-start gap-3">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-medium text-primary-foreground">
            {step.step_number}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-base font-medium leading-snug text-foreground">{step.title}</p>
            {step.body && (
              <p className="mt-0.5 whitespace-pre-line text-sm text-muted-foreground">
                {step.body}
              </p>
            )}
          </div>
        </div>
        {step.caution && (
          <div className="flex items-start gap-2 rounded-lg border border-fresh-salmon/60 bg-fresh-salmon/15 px-3 py-2 text-sm text-fresh-salmon-dark">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{step.caution}</span>
          </div>
        )}
        <div className="flex items-center justify-center gap-1.5 pt-1" aria-hidden>
          {steps.map((s, i) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setIdx(i)}
              className={cn(
                'h-2.5 rounded-full transition-all',
                i === idx ? 'w-6 bg-primary' : 'w-2.5 bg-border hover:bg-muted-foreground/40',
              )}
              tabIndex={-1}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function StepImage({ path, alt }: { path: string | null; alt: string }) {
  const { data: url, isLoading } = useSignedUrl('work-instructions', path);
  if (!path) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-muted-foreground">
        <ImageOff className="h-8 w-8" />
        <span className="text-xs">No photo for this step</span>
      </div>
    );
  }
  if (isLoading || !url) return <Skeleton className="h-full w-full rounded-none" />;
  return <img src={url} alt={alt} className="h-full w-full object-contain" draggable={false} />;
}
